// Dependencies
import { useTranslation } from 'react-i18next';
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Core & Lab
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Accordion from '@mui/material/Accordion';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
// Icons
import Close from '@mui/icons-material/Close';
// Actions
import { updatePicturesForm } from './redux/actions';
// Types
import { AppState } from '../../../../Reducers/Reducers';
import { PictureForm } from './objects/picture';
import { isNumber } from 'lodash';
import { Autocomplete, DialogActions, FormControl, FormHelperText, TextField } from '@mui/material';
import { PictureTypes } from './objects/pictureState';
import { useSnackbar } from 'notistack';
// import { PictureEditAlt } from './pictureEditAlt';
// import { PictureEditCredits } from './pictureEditCredits';

type Props = { picture_index: number | null; setEditPictureIndex: React.Dispatch<React.SetStateAction<number | null>> };


const PictureModalUploadEdit: FC<Props> = ({ picture_index, setEditPictureIndex }): ReactElement => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const pictures = useSelector((state: AppState) => state.picture.files);
    const initial_picture = useMemo(() => isNumber(picture_index) ? pictures?.[picture_index] : null, [picture_index, pictures]);
    const [picture, setPicture] = useState<PictureForm | null>(initial_picture);

    const [picture_name_error, setPictureNameError] = useState('');

    const onChangeField = (value: any, key: string) => {
        if (key === 'name') { setPictureNameError(''); }
        setPicture(state => (state ? { ...state, [key]: value } : null));
    };
    const onClose = () => {
        setEditPictureIndex(null);
    };
    const onSave = () => {
        if (!picture?.name) { setPictureNameError(t('shared.picture-name-required')); enqueueSnackbar(t('shared.picture-name-required'), { variant: 'error' }); return; }
        dispatch(updatePicturesForm(pictures?.map((p, i) => (i === picture_index ? picture : p)) ?? []));
        onClose();
    };
    useEffect(() => {
        setPicture(initial_picture);
    }, [initial_picture]);
    return (
        <Dialog fullWidth open={isNumber(picture_index)} onClose={onClose}>
            <AppBar className={'ft-user-appBar'}>
                <Toolbar>
                    <IconButton edge={'start'} color={'inherit'} onClick={onClose}><Close /></IconButton>
                    <Typography variant={'h6'}>{t('shared.update-picture')}</Typography>
                </Toolbar>
            </AppBar>
            <Container className={'ft-container'} maxWidth={'md'}>
                <Accordion square expanded>
                    <AccordionSummary className={'ft-user-title'}>
                        <Typography>{t('shared.picture-informations')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={'ft-user-subContainer'}>
                        <Grid container spacing={2}>
                            {/* {picture !== null && picture.url !== null && <PictureCropper picture={picture} />} */}
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={PictureTypes}
                                    getOptionLabel={(option) => t(`shared.picture-type.${option}`)}
                                    renderOption={(props, option) => <li {...props}>{t(`shared.picture-type.${option}`)}</li>}
                                    noOptionsText={t('list.autocomplete_no_options_text')}
                                    loadingText={t('list.autocomplete_loading_text')}
                                    renderInput={(params) => <TextField {...params} label={t('shared.type')} />}
                                    value={picture?.type ?? 'PICTURE'}
                                    onChange={(event, value) => onChangeField(value, 'type')}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth required error={Boolean(picture_name_error)}>
                                    <TextField
                                        required
                                        value={picture?.name ?? ''}
                                        onChange={(event) => {
                                            onChangeField(event.target.value, 'name');
                                        }}
                                        label={t('shared.name')}
                                        fullWidth
                                        error={Boolean(picture_name_error)}
                                    />
                                    <FormHelperText>{picture_name_error}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={picture?.description ?? ''}
                                    onChange={(event) => {
                                        onChangeField(event.target.value, 'description');
                                    }}
                                    label={t('shared.description')}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={picture?.alt ?? ''}
                                    onChange={(event) => {
                                        onChangeField(event.target.value, 'alt');
                                    }}
                                    label={t('shared.picture-alt')}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={picture?.credits ?? ''}
                                    onChange={(event) => {
                                        onChangeField(event.target.value, 'credits');
                                    }}
                                    label={t('shared.picture-credit')}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
                <Grid container spacing={2} justifyContent={'flex-end'}>
                    <Grid item>
                    </Grid>
                    <Grid item>
                    </Grid>
                </Grid>
            </Container>
            <DialogActions>
                <Button onClick={onClose}>{t('shared.cancel')}</Button>
                <Button onClick={onSave}>{t('shared.save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PictureModalUploadEdit;